/**
 * Line management actions (W2-07 lines-panel; DESIGN.md §4.10, §4.21, §5.5): the imperative side shared by the
 * registered commands, the Lines panel, the status-bar host and the dialogs. Every document change runs a core
 * command body (`Core.lines`, `Core.clipboard`, `Core.keys`, `Core.doc`) inside one `DocStore.transact`, so each
 * user action is exactly one undo step; failures and notes become toasts.
 *
 * Rules applied here (not in core, because they are editor aids):
 *  - Locked lines (preview filter `lockedLines`) refuse structural edits: delete, rename, type, parent, group,
 *    draw order, export order. Deleting a line also refuses when it would change a locked line: a locked child
 *    (every children policy gives it another parent) or, with "Delete them too", a locked descendant. Lock never
 *    changes the preview or the export.
 *  - Hide / solo / lock / focus hitsounds / pin as lane / also show are editor-store state: never undoable, never
 *    exported (the "Preview differs from export" banner is W2-02's; lock and focus hitsounds never count).
 *  - Pasting whole lines into another project resolves assets this project lacks from the content-addressed store
 *    (`persist.getAsset`): they are added to the project in the same transaction; files the store does not have are
 *    left out and reported.
 */

import { DIALOG } from '../../app/dialogs/types';
import type { AppServices } from '../../app/registry/types';
import { Core } from '../../core/commands';
import type { ClipboardPayload, LineCommands, LinePreset } from '../../core/commands/types';
import { buildTempoMap } from '../../core/doc/container';
import type { AssetId, AssetRef, AssetRole, GroupId, LineId, UiElement } from '../../core/model/types';
import type { TxBuilder } from '../../core/ops/types';
import type { Sec } from '../../core/time/types';
import type { MessageRef } from '../../core/util/types';
import { assetMetaOf, sniffMedia } from '../../formats/package/sniff';
import type { DocStore, EditorStore, TxnMeta, ViewFilterState } from '../../state/types';
import { m } from './lines.messages';
import {
  deleteImpact,
  inExportOrder,
  type DeleteImpact,
  moveInOrder,
  neighbourLine,
  reparentProblem,
  shiftInOrder,
  type ReparentProblem,
} from './lineModel';
import { linesUi, requestReveal, type ParentRequest } from './linesUi';

type AddLinesArgs = Parameters<LineCommands['addLines']>[1];

type Svc = AppServices;

// ---------------------------------------------------------------------------------------------------------------
// Basics
// ---------------------------------------------------------------------------------------------------------------

/** "#3 Arm L" (or "#3" for an unnamed line); "#?" for a line that no longer exists. */
export function lineTitle(store: Pick<DocStore, 'doc' | 'idx'>, id: LineId): string {
  const line = store.doc.lines.get(id);
  if (line === undefined) return '#?';
  const index = String(store.idx.lineIndex(id));
  const name = line.name.trim();
  return name === '' ? `#${index}` : `#${index} ${name}`;
}

/** Playhead (chart seconds) for "keep it where it is now" conversions. */
export function playheadSec(s: Pick<Svc, 'audio'>): Sec {
  try {
    return s.audio.transport.chartTime();
  } catch {
    return 0;
  }
}

/** The reason line commands are unavailable, or null when a chart is open. */
export function noProjectReason(s: Pick<Svc, 'store'>): MessageRef | null {
  return s.store === null ? { key: m.noProject } : null;
}

export interface TransactOptions {
  /** Success toast (with an Undo link). */
  readonly success?: MessageRef;
  /** Toast failures as warnings instead of errors (refusals the user can act on). */
  readonly failKind?: 'warning' | 'error';
}

/**
 * Runs one transaction. Failure reasons and transaction notes become toasts; returns the command's value, or null
 * when it failed or no chart is open.
 */
export function transact<R>(
  s: Svc,
  meta: TxnMeta,
  build: (tx: TxBuilder) => R,
  opts: TransactOptions = {},
): { readonly value: R } | null {
  const store = s.store;
  if (store === null) {
    s.ui.toast({ key: m.noProject }, { kind: 'warning' });
    return null;
  }
  const out = store.transact(meta, build);
  if (!out.ok) {
    s.ui.toast(out.reason, { kind: opts.failKind ?? 'error' });
    return null;
  }
  toastNotes(s, out.notes);
  if (opts.success !== undefined) s.ui.toast(opts.success, { kind: 'success', undo: true });
  return { value: out.value };
}

/** Transaction notes as info toasts, each distinct note once (a step may report the same fact twice). */
function toastNotes(s: Pick<Svc, 'ui'>, notes: readonly MessageRef[]): void {
  const seen = new Set<string>();
  for (const note of notes) {
    const key = JSON.stringify(note);
    if (seen.has(key)) continue;
    seen.add(key);
    s.ui.toast(note, { kind: 'info' });
  }
}

/** The user's shortcut for a command as shown in their keyboard layout (first binding), or null when unbound. */
export function shortcutLabel(s: Pick<Svc, 'keymap'>, command: string): string | null {
  try {
    const chord = s.keymap.bindingsFor(command)[0];
    return chord === undefined ? null : s.keymap.label(chord);
  } catch {
    return null;
  }
}

/** Lines the commands act on: the selected lines (export order), else the active line. */
export function targetLines(s: Pick<Svc, 'store' | 'editor'>): LineId[] {
  const store = s.store;
  if (store === null) return [];
  const st = s.editor.getState();
  const selected = inExportOrder(store.doc, st.selection.lines);
  if (selected.length > 0) return selected;
  const active = st.activeLine;
  return active !== null && store.doc.lines.has(active) ? [active] : [];
}

/** Refusal for structural edits of locked lines, or null. */
export function lockedReason(s: Pick<Svc, 'store' | 'editor'>, ids: readonly LineId[]): MessageRef | null {
  const store = s.store;
  if (store === null) return null;
  const locked = s.editor.getState().filters.lockedLines;
  const hits = ids.filter((id) => locked.has(id));
  const first = hits[0];
  if (first === undefined) return null;
  return { key: m.lockedRefusal, params: { line: lineTitle(store, first), count: hits.length } };
}

/** Toasts the lock refusal; true when the action must stop. */
function refuseLocked(s: Svc, ids: readonly LineId[]): boolean {
  const reason = lockedReason(s, ids);
  if (reason === null) return false;
  s.ui.toast(reason, { kind: 'warning' });
  return true;
}

// ---------------------------------------------------------------------------------------------------------------
// Active line and selection
// ---------------------------------------------------------------------------------------------------------------

/** Selects lines (the N LINES inspector for several); the primary is the last clicked. */
export function selectLines(
  s: Pick<Svc, 'editor'>,
  ids: readonly LineId[],
  primary: LineId | null,
  mode: 'replace' | 'add' | 'toggle' = 'replace',
): void {
  s.editor
    .getState()
    .select({ lines: new Set(ids), ...(primary === null ? {} : { primary: { kind: 'line', id: primary } }) }, mode);
}

export interface ActivateOptions {
  /** Also make it the only selected line (row click). */
  readonly select?: boolean;
  /** Scroll the Lines panel to it. */
  readonly reveal?: boolean;
  /** Short on-screen label (Ctrl+wheel switching). */
  readonly announce?: boolean;
}

/**
 * Makes `id` the active line (lane, motion columns and preview follow). A selection that consists of lines only
 * follows the active line, so the inspector shows it; notes or keys stay selected otherwise.
 */
export function activateLine(s: Svc, id: LineId, opts: ActivateOptions = {}): void {
  const store = s.store;
  if (store === null || !store.doc.lines.has(id)) return;
  const st: EditorStore = s.editor.getState();
  st.setActiveLine(id);
  const sel = st.selection;
  const linesOnly = sel.lines.size > 0 && sel.notes.size === 0 && sel.keys.size === 0 && sel.segments.size === 0;
  if (opts.select === true || linesOnly) selectLines(s, [id], id);
  if (opts.reveal === true) requestReveal(linesUi(s), id);
  if (opts.announce === true) {
    s.ui.toast({ key: m.switchedTo, params: { line: lineTitle(store, id) } }, { kind: 'info', durationMs: 1200 });
  }
}

/** Steps through the export order (wrapping); used by Ctrl+Up / Ctrl+Down. */
export function stepActiveLine(s: Svc, dir: -1 | 1): LineId | null {
  const store = s.store;
  if (store === null) return null;
  const next = neighbourLine(store.doc.lineOrder, s.editor.getState().activeLine, dir);
  if (next !== null) activateLine(s, next, { reveal: true });
  return next;
}

/** RPE preset Ctrl+wheel (nothing selected): one line per wheel notch, clamped at both ends, with a label. */
export function wheelSwitch(s: Svc, notches: number): LineId | null {
  const store = s.store;
  if (store === null || !Number.isFinite(notches)) return null;
  const ui = linesUi(s);
  ui.wheelRest += notches;
  const steps = Math.trunc(ui.wheelRest);
  if (steps === 0) return null;
  ui.wheelRest -= steps;
  const order = store.doc.lineOrder;
  if (order.length === 0) return null;
  const active = s.editor.getState().activeLine;
  const at = active === null ? -1 : order.indexOf(active);
  const from = at < 0 ? (steps > 0 ? -1 : order.length) : at;
  const to = Math.max(0, Math.min(order.length - 1, from + steps));
  const id = order[to] ?? null;
  if (id === null || id === active) return null;
  activateLine(s, id, { reveal: true, announce: true });
  return id;
}

// ---------------------------------------------------------------------------------------------------------------
// Creation
// ---------------------------------------------------------------------------------------------------------------

export interface NewLineInput {
  readonly name?: string;
  readonly texture?: AssetId;
  readonly attachUI?: UiElement;
  readonly text?: string;
  readonly parent?: LineId | null;
}

const PRESET_TYPE: Readonly<Record<LinePreset, string>> = {
  standard: 'line',
  helper: 'helper',
  text: 'text',
  image: 'image',
  gif: 'gif',
  paint: 'paint',
  uiMover: 'uiMover',
};

/** Adds one line of a preset at the end of the export order (it becomes active and selected). */
export function addLine(s: Svc, preset: LinePreset, input: NewLineInput = {}): LineId | null {
  const r = transact(
    s,
    { label: { key: m.txAddLine, params: { type: PRESET_TYPE[preset] } }, command: 'lines.add' },
    (tx) =>
      Core.lines.addLine(tx, {
        preset,
        ...(input.name !== undefined ? { name: input.name } : {}),
        ...(input.texture !== undefined ? { texture: input.texture } : {}),
        ...(input.attachUI !== undefined ? { attachUI: input.attachUI } : {}),
        ...(input.text !== undefined ? { text: input.text } : {}),
        ...(input.parent !== undefined ? { parent: input.parent } : {}),
      }),
  );
  if (r === null) return null;
  requestReveal(linesUi(s), r.value);
  return r.value;
}

/** "New N lines...": `count` lines of one preset at the end of the export order. */
export function addManyLines(s: Svc, args: AddLinesArgs): LineId[] | null {
  const r = transact(
    s,
    { label: { key: m.txAddLines, params: { count: args.count } }, command: 'lines.addMany' },
    (tx) => Core.lines.addLines(tx, args),
    { success: { key: m.addedLines, params: { count: args.count } } },
  );
  if (r === null) return null;
  const first = r.value[0];
  if (first !== undefined) requestReveal(linesUi(s), first);
  return r.value;
}

/** Number of lines of the RPE-style layout. */
export const RPE_LINE_COUNT = 24;

/** RPE-style 24 lines: line 0 visible, lines 1-23 at opacity 0 (DESIGN.md §5.1 Start step). */
export function addRpeLines(s: Svc): LineId[] | null {
  const r = transact(
    s,
    { label: { key: m.txAddLines, params: { count: RPE_LINE_COUNT } }, command: 'lines.addRpe24' },
    (tx) => {
      const ids = Core.lines.addLines(tx, { count: RPE_LINE_COUNT, namePattern: '', preset: 'standard' });
      for (const id of ids.slice(1)) {
        const track = tx.idx.track(id, 'alpha', 0);
        const keys = track === undefined ? [] : tx.idx.keysByTrack(track);
        if (keys.length > 0) Core.keys.setKeyValues(tx, { keys: [...keys], part: 'both', value: 0 });
      }
      return ids;
    },
    { success: { key: m.addedLines, params: { count: RPE_LINE_COUNT } } },
  );
  if (r === null) return null;
  const first = r.value[0];
  if (first !== undefined) requestReveal(linesUi(s), first);
  return r.value;
}

export interface DuplicateOptions {
  readonly withNotes: boolean;
  readonly withChildren: boolean;
  readonly asChild: boolean;
}

/** Duplicates the lines (each copy right after its original); the copies become selected. */
export function duplicateLines(s: Svc, ids: readonly LineId[], opts: DuplicateOptions): LineId[] | null {
  if (ids.length === 0) {
    s.ui.toast({ key: m.nothingSelected }, { kind: 'info' });
    return null;
  }
  const r = transact(
    s,
    { label: { key: m.txDuplicate, params: { count: ids.length } }, command: 'lines.duplicate' },
    (tx) => {
      const tops: LineId[] = [];
      for (const id of ids) tops.push(Core.lines.duplicateLine(tx, { id, ...opts }));
      const first = tops[0];
      tx.setSelection({ lines: tops, ...(first !== undefined ? { activeLine: first } : {}) });
      return tops;
    },
  );
  if (r === null) return null;
  const first = r.value[0];
  if (first !== undefined) requestReveal(linesUi(s), first);
  return r.value;
}

// ---------------------------------------------------------------------------------------------------------------
// Deletion
// ---------------------------------------------------------------------------------------------------------------

export type ChildrenPolicy = 'detach' | 'reparentToGrandparent' | 'deleteSubtree';

/** The children policies in the order the delete dialog lists them. */
export const CHILDREN_POLICIES: readonly ChildrenPolicy[] = ['detach', 'reparentToGrandparent', 'deleteSubtree'];

/**
 * Lines a delete with `policy` changes besides the deleted ones: every surviving child gets another parent (none,
 * or the nearest surviving ancestor); "Delete them too" deletes every descendant instead.
 */
export function deleteTouches(impact: DeleteImpact, policy: ChildrenPolicy): readonly LineId[] {
  return policy === 'deleteSubtree' ? impact.subtree : impact.children;
}

/**
 * Refusal for deleting `impact.lines` with `policy`: one of them is locked, or a line the policy changes is (a
 * locked child, or a locked descendant with "Delete them too"). Null when the delete may go ahead.
 */
export function deleteLockedReason(
  s: Pick<Svc, 'store' | 'editor'>,
  impact: DeleteImpact,
  policy: ChildrenPolicy,
): MessageRef | null {
  const store = s.store;
  if (store === null) return null;
  const own = lockedReason(s, impact.lines);
  if (own !== null) return own;
  const locked = s.editor.getState().filters.lockedLines;
  const hit = deleteTouches(impact, policy).find((id) => locked.has(id));
  if (hit === undefined) return null;
  return {
    key: policy === 'deleteSubtree' ? m.lockedDescendant : m.lockedChild,
    params: { line: lineTitle(store, hit) },
  };
}

/** Deletes lines with a children policy (one undoable step). Returns the failure reason, or null on success. */
export function deleteLinesNow(s: Svc, ids: readonly LineId[], children: ChildrenPolicy): MessageRef | null {
  const store = s.store;
  if (store === null) return { key: m.noProject };
  const locked = deleteLockedReason(s, deleteImpact(store.doc, store.idx, ids), children);
  if (locked !== null) return locked;
  const title = ids.length === 1 && ids[0] !== undefined ? lineTitle(store, ids[0]) : '';
  const out = store.transact(
    { label: { key: m.txDelete, params: { count: ids.length } }, command: 'lines.delete' },
    (tx) => {
      Core.lines.deleteLines(tx, { ids, children });
    },
  );
  if (!out.ok) return out.reason;
  toastNotes(s, out.notes);
  s.ui.toast({ key: m.deleted, params: { count: ids.length, line: title } }, { kind: 'success', undo: true });
  return null;
}

/**
 * Delete key / context menu / header button: lines with notes or children go through the delete dialog (children
 * policy); others are deleted at once (undoable, with an Undo link). Refused with the reason when a deleted line is
 * locked, or when every children policy would change a locked line (a locked child).
 */
export async function deleteFlow(s: Svc, ids: readonly LineId[]): Promise<boolean> {
  const store = s.store;
  if (store === null) return false;
  const lines = inExportOrder(store.doc, ids);
  if (lines.length === 0) {
    s.ui.toast({ key: m.nothingSelected }, { kind: 'info' });
    return false;
  }
  if (refuseLocked(s, lines)) return false;
  const impact = deleteImpact(store.doc, store.idx, lines);
  const refusals = CHILDREN_POLICIES.map((policy) => deleteLockedReason(s, impact, policy));
  const firstRefusal = refusals[0];
  if (firstRefusal !== null && firstRefusal !== undefined && refusals.every((r) => r !== null)) {
    s.ui.toast(firstRefusal, { kind: 'warning' });
    return false;
  }
  if (impact.notes > 0 || impact.children.length > 0) {
    const res = await s.ui.openDialog(DIALOG.lineDelete, { lines });
    return res !== null;
  }
  const reason = deleteLinesNow(s, lines, 'detach');
  if (reason !== null) s.ui.toast(reason, { kind: 'error' });
  return reason === null;
}

// ---------------------------------------------------------------------------------------------------------------
// Structure: parents, export order, draw order, names, groups
// ---------------------------------------------------------------------------------------------------------------

/**
 * Coalescing key of the parent prompt's own "Also turn with parent" change, used only when another package opened
 * the prompt (the Lines parent flow applies the switch inside its own transaction): a caller that uses the same key
 * for its parent change gets one undo step for both.
 */
export function parentCoalesceKey(line: LineId): string {
  return `lines.parent:${String(line)}`;
}

/** Message of a refused reparent ("would make Rotor its own ancestor"). */
export function reparentProblemMessage(store: Pick<DocStore, 'doc' | 'idx'>, p: ReparentProblem): MessageRef | null {
  switch (p.kind) {
    case 'cycle':
      return { key: m.wouldLoop, params: { line: lineTitle(store, p.line), parent: lineTitle(store, p.parent) } };
    case 'missing':
      return { key: m.lineGone };
    case 'unchanged':
      return null;
  }
}

/**
 * One line's part of a parent change: the parent (with the chosen mode) and, when `turn` is not null, "Also turn
 * with parent". Core's keep-place conversion keeps the on-screen angle with the line's current switch, so a changed
 * switch is set while the line has no parent: a line that has one is first detached in place (exact), then the
 * switch changes (nothing moves without a parent), then it is attached in place with the new switch (exact).
 */
export function applyParentChange(
  tx: TxBuilder,
  args: {
    readonly line: LineId;
    readonly parent: LineId | null;
    readonly mode: 'keepNumbers' | 'keepPlace';
    readonly atSec: Sec;
    readonly turn: boolean | null;
  },
): void {
  const { line: id, parent, mode, atSec, turn } = args;
  const line = tx.doc.lines.get(id);
  if (line === undefined) return;
  if (turn === null || line.rotateWithParent === turn) {
    Core.lines.setParent(tx, { line: id, parent, mode, atSec });
    return;
  }
  if (mode === 'keepPlace' && line.parent !== null && line.parent !== parent) {
    Core.lines.setParent(tx, { line: id, parent: null, mode, atSec });
  }
  Core.lines.patchLine(tx, { id, patch: { rotateWithParent: turn } });
  Core.lines.setParent(tx, { line: id, parent, mode, atSec });
}

/**
 * Sets the parent of `lines` (null = detach) after the parent prompt (keep numbers / keep place, also turn with
 * parent); `order` is an export order to apply in the same step (drag between rows). Everything, including a changed
 * "Also turn with parent" for every line, is one transaction: one undo step, nothing applied when it fails. Returns
 * true when something changed.
 */
export async function setParentFlow(
  s: Svc,
  lines: readonly LineId[],
  parent: LineId | null,
  order: readonly LineId[] | null = null,
): Promise<boolean> {
  const store = s.store;
  if (store === null) return false;
  const ids = inExportOrder(store.doc, lines);
  if (ids.length === 0) return false;
  const problem = reparentProblem(store.doc, ids, parent);
  if (problem !== null && problem.kind !== 'unchanged') {
    const msg = reparentProblemMessage(store, problem);
    if (msg !== null) s.ui.toast(msg, { kind: 'warning' });
    return false;
  }
  if (refuseLocked(s, ids)) return false;
  if (problem?.kind === 'unchanged') return order === null ? false : reorderLines(s, order, ids);
  const first = ids.find((id) => store.doc.lines.get(id)?.parent !== parent) ?? ids[0];
  if (first === undefined) return false;
  // The prompt's props name one line and its result is only the mode (CR-2): the full request waits here.
  const ui = linesUi(s);
  const answer: { turn: boolean | null } = { turn: null };
  const request: ParentRequest = {
    line: first,
    parent,
    lines: ids,
    answerTurn: (turn) => {
      answer.turn = turn;
    },
  };
  ui.parentRequest = request;
  let mode: 'keepNumbers' | 'keepPlace' | null;
  try {
    mode = await s.ui.openDialog(DIALOG.parentPrompt, { line: first, parent });
  } finally {
    if (ui.parentRequest === request) ui.parentRequest = null;
  }
  if (mode === null) return false;
  const chosen = mode;
  const turn = parent === null ? null : answer.turn;
  const atSec = playheadSec(s);
  const r = transact(
    s,
    {
      label: { key: parent === null ? m.txDetach : m.txSetParent, params: { count: ids.length } },
      command: 'lines.setParent',
      focus: { line: first },
    },
    (tx) => {
      if (order !== null) Core.lines.reorderLines(tx, { order });
      for (const id of ids) applyParentChange(tx, { line: id, parent, mode: chosen, atSec, turn });
    },
  );
  return r !== null;
}

/** Applies a new export order (references are ids: nothing breaks). */
export function reorderLines(s: Svc, order: readonly LineId[], moved: readonly LineId[]): boolean {
  if (refuseLocked(s, moved)) return false;
  return (
    transact(s, { label: { key: m.txReorder, params: { count: moved.length } }, command: 'lines.reorder' }, (tx) => {
      Core.lines.reorderLines(tx, { order });
    }) !== null
  );
}

/** Moves lines one step up (-1) or down (+1) in the export order, as a block. */
export function moveLinesBy(s: Svc, ids: readonly LineId[], dir: -1 | 1): boolean {
  const store = s.store;
  if (store === null || ids.length === 0) return false;
  const next = shiftInOrder(store.doc.lineOrder, ids, dir);
  if (next === null) return false;
  return reorderLines(s, next, ids);
}

/** Moves lines before / after a target line in the export order. */
export function moveLinesTo(s: Svc, ids: readonly LineId[], target: LineId, where: 'before' | 'after'): boolean {
  const store = s.store;
  if (store === null) return false;
  const next = moveInOrder(store.doc.lineOrder, ids, target, where);
  if (next === null) return false;
  return reorderLines(s, next, ids);
}

export function renameLine(s: Svc, id: LineId, name: string): boolean {
  const store = s.store;
  const line = store?.doc.lines.get(id);
  if (store === null || line === undefined) return false;
  const next = name.trim();
  if (next === line.name) return false;
  if (refuseLocked(s, [id])) return false;
  return (
    transact(
      s,
      { label: { key: m.txRename, params: { name: next } }, command: 'lines.rename', focus: { line: id } },
      (tx) => {
        Core.lines.patchLine(tx, { id, patch: { name: next } });
      },
    ) !== null
  );
}

/** Bring to front / send to back (z = max + 1 / min - 1 among the other lines). */
export function setZExtreme(s: Svc, ids: readonly LineId[], where: 'front' | 'back'): boolean {
  if (ids.length === 0 || refuseLocked(s, ids)) return false;
  return (
    transact(
      s,
      {
        label: { key: where === 'front' ? m.txToFront : m.txToBack, params: { count: ids.length } },
        command: 'lines.z',
      },
      (tx) => {
        Core.lines.setZExtreme(tx, { ids, where });
      },
    ) !== null
  );
}

/** Puts lines into a group (null = no group, RPE "Default"). */
export function setGroup(s: Svc, ids: readonly LineId[], group: GroupId | null): boolean {
  if (ids.length === 0 || refuseLocked(s, ids)) return false;
  return (
    transact(s, { label: { key: m.txSetGroup, params: { count: ids.length } }, command: 'lines.setGroup' }, (tx) => {
      for (const id of ids) Core.lines.patchLine(tx, { id, patch: { group } });
    }) !== null
  );
}

/** Creates a group (optionally putting `ids` into it) in one step. */
export function createGroup(s: Svc, name: string, ids: readonly LineId[] = []): GroupId | null {
  if (refuseLocked(s, ids)) return null;
  const r = transact(
    s,
    { label: { key: m.txNewGroup, params: { name: name.trim() } }, command: 'lines.newGroup' },
    (tx) => {
      const g = Core.lines.addGroup(tx, { name });
      for (const id of ids) Core.lines.patchLine(tx, { id, patch: { group: g } });
      return g;
    },
  );
  return r === null ? null : r.value;
}

/** Opens the line type dialog (data loss is explained there; the change is undoable). */
export async function changeTypeFlow(s: Svc, id: LineId): Promise<boolean> {
  if (s.store === null || refuseLocked(s, [id])) return false;
  const res = await s.ui.openDialog(DIALOG.lineType, { line: id });
  return res !== null;
}

// ---------------------------------------------------------------------------------------------------------------
// Preview-only aids (editor store; never undoable, never exported)
// ---------------------------------------------------------------------------------------------------------------

export type FilterSet = 'hiddenLines' | 'soloLines' | 'lockedLines' | 'focusHitsoundLines';

/** Adds `ids` to a filter set, or removes them when all are already in it. Returns the new on/off state. */
export function toggleFilter(s: Pick<Svc, 'editor'>, set: FilterSet, ids: readonly LineId[]): boolean {
  const st = s.editor.getState();
  const cur = st.filters[set];
  const allOn = ids.length > 0 && ids.every((id) => cur.has(id));
  const next = new Set(cur);
  for (const id of ids) {
    if (allOn) next.delete(id);
    else next.add(id);
  }
  const patch: Partial<ViewFilterState> = { [set]: next };
  st.setFilters(patch);
  return !allOn;
}

/** "Show all": clears hide, solo, layer mute and dim (everything that makes the preview differ from the export). */
export function showAll(s: Pick<Svc, 'editor'>): void {
  s.editor.getState().setFilters({
    hiddenLines: new Set(),
    soloLines: new Set(),
    mutedLayers: new Map(),
    dimOthers: 0,
  });
}

export function unlockAll(s: Pick<Svc, 'editor'>): void {
  s.editor.getState().setFilters({ lockedLines: new Set() });
}

/** Pins / unpins lines as extra lanes (multi-line editing). */
export function togglePinnedLanes(s: Pick<Svc, 'editor'>, ids: readonly LineId[]): boolean {
  const st = s.editor.getState();
  const cur = st.pinnedLanes;
  const allOn = ids.length > 0 && ids.every((id) => cur.includes(id));
  st.setPinnedLanes(allOn ? cur.filter((id) => !ids.includes(id)) : [...cur, ...ids.filter((id) => !cur.includes(id))]);
  return !allOn;
}

/** Adds / removes lines whose notes the lane draws as ghosts ("Also show", VIEW-06). */
export function toggleAlsoShow(s: Pick<Svc, 'editor'>, ids: readonly LineId[]): boolean {
  const st = s.editor.getState();
  const cur = st.alsoShow;
  const allOn = ids.length > 0 && ids.every((id) => cur.includes(id));
  st.setAlsoShow(allOn ? cur.filter((id) => !ids.includes(id)) : [...cur, ...ids.filter((id) => !cur.includes(id))]);
  return !allOn;
}

// ---------------------------------------------------------------------------------------------------------------
// Whole-line clipboard (DESIGN.md §4.10)
// ---------------------------------------------------------------------------------------------------------------

/** Copies whole lines (record, tracks, notes, asset references) to the app and system clipboards. */
export async function copyLines(s: Svc, ids: readonly LineId[]): Promise<ClipboardPayload | null> {
  const store = s.store;
  if (store === null) return null;
  const lines = inExportOrder(store.doc, ids);
  if (lines.length === 0) {
    s.ui.toast({ key: m.nothingSelected }, { kind: 'info' });
    return null;
  }
  const payload = Core.clipboard.copy(
    { doc: store.doc, idx: store.idx, tempo: buildTempoMap(store.doc, store.idx) },
    { notes: [], keys: [], segments: [], lines },
  );
  if (payload === null) return null;
  await s.clipboard.set(payload);
  const keys = shortcutLabel(s, 'lines.paste');
  s.ui.toast(
    keys === null
      ? { key: m.copied, params: { count: lines.length } }
      : { key: m.copiedKey, params: { count: lines.length, keys } },
    { kind: 'success' },
  );
  return payload;
}

/** Cut = copy, then the delete flow (dialog for lines with notes or children). */
export async function cutLines(s: Svc, ids: readonly LineId[]): Promise<boolean> {
  if (refuseLocked(s, ids)) return false;
  const payload = await copyLines(s, ids);
  if (payload === null) return false;
  return deleteFlow(s, ids);
}

const IMAGE_EXT: Readonly<Record<string, string>> = { png: 'png', jpg: 'jpg', gif: 'gif', webp: 'webp', bmp: 'bmp' };

function extensionOf(codec: string): string {
  if (codec in IMAGE_EXT) return IMAGE_EXT[codec] ?? 'bin';
  if (codec.startsWith('ogg')) return 'ogg';
  if (codec.startsWith('wav')) return 'wav';
  if (codec === 'mp3' || codec === 'flac' || codec === 'aac') return codec;
  return 'bin';
}

function slug(text: string): string {
  const s = text.replace(/[^A-Za-z0-9_-]+/g, '_').replace(/^_+|_+$/g, '');
  return s === '' ? 'line' : s.slice(0, 40);
}

/**
 * Asset records for files the pasted lines use that this project does not have and the content-addressed store
 * (shared by all projects) holds: the source project's record when the clipboard carries it (ClipLine.assetRefs,
 * CR-W2-07-1), else one built by sniffing the bytes (older clipboards). Files the store lacks stay out (core reports
 * them as missing).
 */
export async function resolvePastedAssets(
  s: Pick<Svc, 'store' | 'persist'>,
  payload: ClipboardPayload,
): Promise<AssetRef[]> {
  const store = s.store;
  if (store === null) return [];
  const need = new Map<AssetId, { readonly texture: boolean; readonly hint: string; readonly source?: AssetRef }>();
  for (const cl of payload.wholeLines) {
    for (const a of cl.assets) {
      if (store.doc.assets.has(a) || need.has(a)) continue;
      const source = cl.assetRefs?.find((r) => r.id === a);
      need.set(a, { texture: a === cl.line.texture, hint: cl.line.name, ...(source !== undefined ? { source } : {}) });
    }
  }
  const out: AssetRef[] = [];
  for (const [id, use] of need) {
    try {
      if (!(await s.persist.hasAsset(id))) continue;
      // The source project's record (ClipLine.assetRefs): its own file name, type and metadata; addAsset still
      // sanitises the path and makes it unique in this project.
      if (use.source !== undefined) {
        out.push(use.source);
        continue;
      }
      const bytes = await s.persist.getAsset(id);
      const sniff = sniffMedia(bytes, '');
      const role: AssetRole = use.texture ? (sniff.codec === 'gif' ? 'gif' : 'texture') : 'hitsound';
      const folder = use.texture ? 'textures' : 'sfx';
      out.push({
        id,
        path: `${folder}/${slug(use.hint)}-${id.slice(0, 8)}.${extensionOf(sniff.codec)}`,
        mime: sniff.mime,
        bytes: bytes.length,
        role,
        meta: assetMetaOf(bytes, sniff),
      });
    } catch {
      // Unreadable in the store: core leaves the reference out and reports it.
    }
  }
  return out;
}

/** Pastes whole lines from the clipboard as new lines (after the target lines; also across projects). */
export async function pasteLines(s: Svc): Promise<LineId[] | null> {
  const store = s.store;
  if (store === null) return null;
  const payload = await s.clipboard.read();
  if (payload === null || payload.wholeLines.length === 0) {
    s.ui.toast({ key: payload === null ? m.pasteEmpty : m.pasteNotLines }, { kind: 'info' });
    return null;
  }
  const refs = await resolvePastedAssets(s, payload);
  // The store may have changed while the clipboard and the asset store were read.
  const cur = s.store;
  if (cur === null) return null;
  const targets = targetLines(s);
  const after = targets[targets.length - 1];
  const r = transact(
    s,
    { label: { key: m.txPaste, params: { count: payload.wholeLines.length } }, command: 'lines.paste' },
    (tx) => {
      for (const ref of refs) Core.doc.addAsset(tx, { ref });
      return Core.clipboard.pasteLines(tx, { payload, ...(after !== undefined ? { after } : {}) });
    },
  );
  if (r === null) return null;
  const { lines } = r.value;
  if (refs.length > 0) s.ui.toast({ key: m.assetsResolved, params: { count: refs.length } }, { kind: 'info' });
  s.ui.toast({ key: m.pasted, params: { count: lines.length } }, { kind: 'success', undo: true });
  const first = lines[0];
  if (first !== undefined) requestReveal(linesUi(s), first);
  return lines;
}
